// src/shared/components/DateTimeField.tsx

import { useMemo } from 'react';
import { TextInput } from '@mantine/core';
import { DateTimePicker, type DateTimePickerProps } from '@mantine/dates';

/**
 * Pure conversions between the app's Mantine date-string convention
 * ('yyyy-MM-dd HH:mm:ss', local time — see CLAUDE.md timestamp rules) and the
 * native input[type="datetime-local"] value format ('yyyy-MM-ddTHH:mm').
 * String surgery only — no Date/Luxon, so no timezone can sneak in: both
 * formats express the same local wall-clock time.
 */
export class DateTimeFieldUtils {
  /** App date string → native datetime-local value; '' when absent/invalid. */
  static toNativeValue(value: unknown): string {
    if (typeof value !== 'string') return '';
    const m = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/.exec(value);
    return m ? `${m[1]}T${m[2]}` : '';
  }

  /** Native datetime-local value → app date string; null when empty/invalid. */
  static fromNativeValue(value: string): string | null {
    const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::(\d{2}))?$/.exec(value);
    return m ? `${m[1]} ${m[2]}:${m[3] ?? '00'}` : null;
  }

  /**
   * Mantine minDate/maxDate (date-only 'yyyy-MM-dd' or full date string) →
   * native min/max attribute. A date-only bound covers its whole day.
   */
  static toNativeBound(value: unknown, edge: 'min' | 'max'): string | undefined {
    if (typeof value !== 'string') return undefined;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return `${value}T${edge === 'max' ? '23:59' : '00:00'}`;
    }
    const converted = this.toNativeValue(value);
    return converted || undefined;
  }
}

/**
 * Drop-in replacement for Mantine DateTimePicker that fixes date-time entry
 * on phones. Mantine's picker composes custom hour/minute/AM-PM "spin" text
 * segments; real mobile browsers don't reliably raise the virtual keyboard
 * for them (customer-reported on recording schedules — no way to set a time
 * at all), and Mantine's own docs point mobile time entry at the native
 * element. On coarse-pointer devices this renders a Mantine-styled native
 * input[type="datetime-local"] — one tap opens the OS date/time wheels, no
 * keyboard involved; desktop keeps the Mantine picker unchanged.
 *
 * Desktop-only props (valueFormat, timePickerProps, clearable, ...) are
 * accepted and simply don't apply on the native branch; the native input
 * displays per the device locale. Native minute precision: seconds normalize
 * to :00 on change (matches the picker's own no-seconds entry). Clearing the
 * native field emits null — callers already ignore null (clearable={false}
 * semantics).
 */
export function DateTimeField(props: DateTimePickerProps) {
  // Static per session — pointer class doesn't change mid-visit (same
  // detection as the 3.11.1 upload picker branch).
  const isCoarsePointer = useMemo(() => window.matchMedia('(pointer: coarse)').matches, []);

  if (!isCoarsePointer) {
    return <DateTimePicker {...props} />;
  }

  const {
    label,
    description,
    error,
    disabled,
    required,
    withAsterisk,
    value,
    onChange,
    onBlur,
    minDate,
    maxDate,
  } = props;

  return (
    <TextInput
      type="datetime-local"
      label={label}
      description={description}
      error={error}
      disabled={disabled}
      required={required}
      withAsterisk={withAsterisk}
      value={DateTimeFieldUtils.toNativeValue(value)}
      onChange={(e) =>
        (onChange as ((v: string | null) => void) | undefined)?.(
          DateTimeFieldUtils.fromNativeValue(e.currentTarget.value),
        )
      }
      // Picker props type onBlur for the trigger BUTTON element; RHF's onBlur
      // (the only caller) is element-agnostic, so the retype is safe here.
      onBlur={onBlur as React.FocusEventHandler<HTMLInputElement> | undefined}
      min={DateTimeFieldUtils.toNativeBound(minDate, 'min')}
      max={DateTimeFieldUtils.toNativeBound(maxDate, 'max')}
    />
  );
}
